import { useState } from 'react'
import { ChevronDown, User, Mail, Users } from 'lucide-react'
import GroupChatModal from '@/components/Student/GroupChatModal'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAppSelector, useAppDispatch } from '@/redux/hooks'
import { sendNegotiationInvite } from '@/redux/asyncThunks/negotiationWebsocket'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { useGamePhase } from '@/hooks/useGamePhase'
import { selectRoomPlayers } from '@/redux/selectors/roomSelector'
import { Status } from '@/redux/types/room'
import { NegotiationType } from '@/redux/types/negotiationWebsocket'
import { useTeamName } from '@/hooks/useTeamName'
import { ActivityState } from '@/redux/slices/timerConfigurationSlice'
import { useEffectiveUser } from '@/hooks/useEffectiveUser'
import { cn } from '@/lib/utils'

const InvitePlayerTeamDisplay = ({ teamId }: { teamId: number }) => {
  const teamName = useTeamName(teamId)
  if (!teamName) return null

  return (
    <div className='flex items-center space-x-1 mt-0.5'>
      <div className='w-2 h-2 bg-green-400 rounded-full'></div>
      <span className='text-xs text-gray-500 truncate'>{teamName}</span>
    </div>
  )
}

const InviteDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [groupModalOpen, setGroupModalOpen] = useState(false)
  const roomPlayers = useSelector(selectRoomPlayers)
  const { user } = useEffectiveUser()
  const { negotiations } = useAppSelector((state) => state.negotiationWS)
  const dispatch = useAppDispatch()
  const params = useParams()
  const roomId = params.roomId as string
  const { gameEnded } = useSelector((state: RootState) => state.waitingRoomWS)
  const {
    isNegotiationPhase,
    phaseId,
    manualMode,
    negotiationPhase,
    roundStatus,
  } = useGamePhase()

  const filteredPlayers = roomPlayers.filter(
    (player) => player.member_email !== user?.email
  )

  const getDisplayName = (player: any) => {
    return player.member_name?.trim() || player.member_email
  }

  const isEmailAsName = (player: any) => {
    const displayName = getDisplayName(player)
    return displayName === player.member_email
  }

  const getInviteStatus = (playerEmail: string) => {
    const existingNegotiation = negotiations.find((negotiation) => {
      // Group chats have their own invite flow and must not shadow the
      // one-to-one conference status.
      if (
        negotiation.negotiation_type === NegotiationType.PUBLIC_ROOM ||
        negotiation.negotiation_type === NegotiationType.GROUP_ROOM
      )
        return false

      const currentUserMember = negotiation.members.find(
        (member) => member.email === user?.email
      )
      const targetPlayerMember = negotiation.members.find(
        (member) => member.email === playerEmail
      )

      return currentUserMember && targetPlayerMember
    })

    if (!existingNegotiation) return Status.Invite

    const inviteeMember = existingNegotiation.members.find(
      (member) => member.status !== Status.Owner
    )

    return inviteeMember?.status || Status.Invite
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case Status.Pending:
        return 'text-amber-600 bg-amber-50 border-amber-200'
      case Status.Accepted:
        return 'text-green-600 bg-green-50 border-green-200'
      case Status.Rejected:
        return 'text-red-600 bg-red-50 border-red-200'
      default:
        return 'text-blue-600 bg-blue-50 border-blue-200'
    }
  }

  const handleSelect = (playerEmail: string) => {
    const status = getInviteStatus(playerEmail)
    if (status === Status.Invite || status === Status.Rejected) {
      const phaseIdToUse = manualMode
        ? negotiationPhase?.id.toString()
        : phaseId?.toString()

      dispatch(
        sendNegotiationInvite({
          inviteeEmail: playerEmail,
          roomId,
          phaseId: phaseIdToUse,
        })
      )
    }
  }

  const inviteControlsDisabled =
    !isNegotiationPhase ||
    gameEnded ||
    roundStatus === ActivityState.Completed ||
    roundStatus === ActivityState.Paused

  return (
    <div className='flex h-full flex-col gap-2'>
      <Button
        className={cn(
          'w-full bg-[#E6ECF2] text-black hover:bg-dark-blue hover:text-white shadow-none border border-black justify-between rounded-[7px] transition-all duration-200'
        )}
        disabled={inviteControlsDisabled}
        onClick={() => setGroupModalOpen(true)}
      >
        <span className='flex items-center gap-2'>Group Chat</span>
        <Users className='w-4 h-4 opacity-50 shrink-0' />
      </Button>
      <GroupChatModal
        isOpen={groupModalOpen}
        onClose={() => setGroupModalOpen(false)}
        mode='create'
        phaseId={
          manualMode ? negotiationPhase?.id.toString() : phaseId?.toString()
        }
      />
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            className={cn(
              'h-full w-full bg-[#E6ECF2] text-black hover:bg-dark-blue hover:text-white shadow-none border border-black justify-between rounded-[7px] transition-all duration-200'
            )}
            role='combobox'
            aria-expanded={isOpen}
            disabled={
              !isNegotiationPhase ||
              gameEnded ||
              roundStatus === ActivityState.Completed ||
              roundStatus === ActivityState.Paused
            }
          >
            <span className='flex items-center gap-2'>Conference</span>
            <ChevronDown className='w-4 h-4 opacity-50 shrink-0' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className={cn('w-72 p-2 shadow-lg border-0 bg-white/95 backdrop-blur-sm')}>
          <div className='flex flex-col space-y-1'>
            {filteredPlayers.length > 0 ? (
              <>
                <div className='px-2 py-1 text-xs font-medium text-gray-500 border-b border-gray-100 mb-1'>
                  Available Players ({filteredPlayers.length})
                </div>
                {filteredPlayers.map((player) => {
                  const inviteStatus = getInviteStatus(player.member_email)
                  const displayName = getDisplayName(player)
                  const isClickable =
                    inviteStatus === Status.Invite ||
                    inviteStatus === Status.Rejected

                  return (
                    <button
                      key={player.member_email}
                      onClick={() => handleSelect(player.member_email)}
                      className={cn(
                        'flex items-center justify-between p-1 rounded-lg text-sm transition-all duration-200 group',
                        isClickable && !gameEnded
                          ? 'cursor-pointer hover:bg-blue-50 hover:shadow-sm border border-transparent hover:border-blue-200'
                          : 'opacity-60 cursor-not-allowed bg-gray-50'
                      )}
                      disabled={!isClickable || gameEnded}
                    >
                      <div className='flex items-center min-w-0 flex-1 '>
                        <div className='flex flex-col items-start min-w-0 flex-1'>
                          <div className='flex items-center space-x-2 w-full'>
                            {isEmailAsName(player) ? (
                              <div className='flex items-center space-x-1 min-w-0'>
                                <Mail className='w-3 h-3 text-gray-400 flex-shrink-0' />
                                <span className='font-medium text-gray-700 truncate text-xs'>
                                  {displayName}
                                </span>
                              </div>
                            ) : (
                              <div className='flex items-center space-x-1 min-w-0'>
                                <User className='w-3 h-3 text-gray-400 flex-shrink-0' />
                                <span className='font-medium text-gray-800 truncate'>
                                  {displayName}
                                </span>
                              </div>
                            )}
                          </div>

                          {player?.teamId && (
                            <InvitePlayerTeamDisplay teamId={player.teamId} />
                          )}
                        </div>
                      </div>

                      <div className='flex-shrink-0 ml-2'>
                        <span
                          className={cn(
                            'px-2 py-1 text-xs font-medium rounded-full border transition-colors',
                            getStatusColor(inviteStatus)
                          )}
                        >
                          {inviteStatus}
                        </span>
                      </div>
                    </button>
                  )
                })}
              </>
            ) : (
              <div className='flex flex-col items-center justify-center py-8 text-center'>
                <div className='w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3'>
                  <User className='w-6 h-6 text-gray-400' />
                </div>
                <span className='text-sm font-medium text-gray-500 mb-1'>
                  No players to invite
                </span>
              </div>
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export default InviteDropdown
